import React, { useMemo, useState } from 'react';
import {
  EdgeProps,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
  useReactFlow,
} from '@xyflow/react';

// Edge colors matching the connector colors from BaseNode
const EDGE_COLORS = {
  text: { default: '#3b82f6', hover: '#60a5fa' }, // blue-500, blue-400
  image: { default: '#a855f7', hover: '#c084fc' }, // purple-500, purple-400
  video: { default: '#06b6d4', hover: '#22d3ee' }, // cyan-500, cyan-400
  audio: { default: '#6366f1', hover: '#818cf8' }, // indigo-500, indigo-400
  default: { default: '#6366f1', hover: '#818cf8' }, // indigo-500, indigo-400
};

// Determine data type from handle ID
function getDataTypeFromHandle(handleId: string | null | undefined): string {
  if (!handleId) return 'default';

  const id = handleId.toLowerCase();
  if (id.includes('image') || id === 'first-frame' || id === 'last-frame')
    return 'image';
  if (id.includes('video')) return 'video';
  if (id.includes('audio') || id.includes('speech') || id.includes('sound'))
    return 'audio';
  if (
    id.includes('text') ||
    id.includes('prompt') ||
    id.includes('caption') ||
    id.includes('script')
  )
    return 'text';
  return 'default';
}

export function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  sourceHandleId,
  style = {},
  markerEnd,
}: EdgeProps) {
  const { setEdges } = useReactFlow();
  const [isHovered, setIsHovered] = useState(false);
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const dataType = getDataTypeFromHandle(sourceHandleId);
  const colors = EDGE_COLORS[dataType as keyof typeof EDGE_COLORS];

  const edgeStyle = useMemo(
    () => ({
      ...style,
      stroke: isHovered ? colors.default : colors.hover,
      strokeWidth: isHovered ? 3 : 2.5,
      opacity: isHovered ? 1 : 0.7,
      transition: 'stroke 0.2s, stroke-width 0.2s, opacity 0.2s',
    }),
    [style, isHovered, colors],
  );

  const onEdgeClick = () => {
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  };

  return (
    <>
      <g
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <BaseEdge path={edgePath} markerEnd={markerEnd} style={edgeStyle} />
      </g>
      <EdgeLabelRenderer>
        {isHovered && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              fontSize: 12,
              pointerEvents: 'all',
            }}
            className='nodrag nopan'
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <button
              className='w-5 h-5 bg-white border-2 border-gray-300 rounded-full hover:bg-red-50 hover:border-red-500 transition-colors flex items-center justify-center group shadow-md'
              onClick={onEdgeClick}
              title='Remove connection'
            >
              <svg
                className='w-3 h-3 text-gray-500 group-hover:text-red-600'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
}
